/**
 * Notification Preferences Screen
 *
 * Per-user toggles for each notification type, grouped by role.
 * Reads/writes to notification_preferences table.
 */

import { useState, useEffect } from 'react';
import { View, Text, ScrollView, Switch, ActivityIndicator } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Stack, useRouter } from 'expo-router';
import { ChevronLeft } from 'lucide-react-native';
import Animated, { FadeInDown } from 'react-native-reanimated';
import { useThemeColors } from '@/lib/useThemeColors';
import { isWeb } from '@/lib/platform';
import { AppLayout } from '@/components/AppLayout';
import { useAuth } from '@/lib/supabase';
import { supabase } from '@/lib/supabase/client';
import { useProfileStore } from '@/lib/state/profile-store';
import { mode } from '@/lib/config/app-mode';
import { reportError } from '@/lib/errors';
import AnimatedPressable from '@/components/AnimatedPressable';

// Preference keys and their display config
interface PrefItem {
  key: string;
  label: string;
  description: string;
}

// Push toggle — shown for all roles
const DELIVERY_PREFS: PrefItem[] = [
  { key: 'push_enabled', label: 'Push Notifications', description: 'Receive notifications on your lock screen and as banners' },
];

// Role-specific notification preferences — each role only sees what's relevant to them
const PREFS_BY_ROLE: Record<string, { title: string; items: PrefItem[] }[]> = {
  startup: [
    {
      title: 'Your Startup',
      items: [
        { key: 'score_updates', label: 'Score Updates', description: 'When your Inverio Score is computed or updated' },
        { key: 'investor_matches', label: 'Investor Matches', description: 'When new matching investors are found' },
        { key: 'data_room_requests', label: 'Data Room Requests', description: 'When an investor requests data room access' },
        { key: 'document_uploads', label: 'Document Activity', description: 'When investors interact with your data room' },
      ],
    },
    {
      title: 'Communication',
      items: [
        { key: 'dd_messages', label: 'Due Diligence Messages', description: 'New messages in DD conversations about your company' },
        { key: 'team_updates', label: 'Team', description: 'When someone accepts a team invitation' },
        { key: 'feedback_updates', label: 'Feedback Responses', description: 'When admin responds to your feedback' },
      ],
    },
    {
      title: 'Platform',
      items: [
        { key: 'system_updates', label: 'System Updates', description: 'Platform announcements and maintenance' },
      ],
    },
  ],
  investor: [
    {
      title: 'Deal Flow',
      items: [
        { key: 'dd_invites', label: 'DD Invitations', description: 'Invitations to shared due diligence sessions' },
        { key: 'data_room_access', label: 'Data Room Access', description: 'When a startup grants or declines access' },
        { key: 'deck_analysis', label: 'Pitch Deck Analysis', description: 'When your pitch deck analysis completes' },
      ],
    },
    {
      title: 'Communication',
      items: [
        { key: 'dd_messages', label: 'Due Diligence Messages', description: 'New messages in DD conversations' },
        { key: 'team_updates', label: 'Team', description: 'When someone accepts a team invitation' },
        { key: 'feedback_updates', label: 'Feedback Responses', description: 'When admin responds to your feedback' },
      ],
    },
    {
      title: 'Platform',
      items: [
        { key: 'system_updates', label: 'System Updates', description: 'Platform announcements and maintenance' },
      ],
    },
  ],
  admin: [
    {
      title: 'Admin Alerts',
      items: [
        { key: 'new_paid_users', label: 'New Paid Users', description: 'When a user subscribes to a paid plan' },
        { key: 'new_waitlist_signups', label: 'Waitlist Signups', description: 'When someone joins the waitlist' },
        { key: 'feedback_updates', label: 'New Feedback', description: 'When users submit feedback or bug reports' },
      ],
    },
    {
      title: 'Platform',
      items: [
        { key: 'system_updates', label: 'System Updates', description: 'Platform announcements and maintenance' },
      ],
    },
  ],
};

type Prefs = Record<string, boolean>;

// Collect all unique pref keys across all roles for defaults
function getAllPrefKeys(): string[] {
  const keys = new Set<string>();
  DELIVERY_PREFS.forEach((p) => keys.add(p.key));
  Object.values(PREFS_BY_ROLE).forEach((sections) =>
    sections.forEach((s) => s.items.forEach((p) => keys.add(p.key)))
  );
  return Array.from(keys);
}

export default function NotificationSettingsScreen() {
  const router = useRouter();
  const colors = useThemeColors();
  const { user } = useAuth();
  const userRole = useProfileStore((s) => s.userRole);
  const [prefs, setPrefs] = useState<Prefs | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);

  const userId = user?.id;

  // Load preferences
  useEffect(() => {
    if (!userId || mode.isMock) {
      // Mock defaults
      const defaults: Prefs = {};
      getAllPrefKeys().forEach((k) => { defaults[k] = true; });
      setPrefs(defaults);
      setLoading(false);
      return;
    }

    (async () => {
      const { data, error } = await supabase
        .from('notification_preferences')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();

      if (error) {
        reportError(error, { context: 'NotificationSettings.load', silent: true });
      }

      if (data) {
        setPrefs(data as unknown as Prefs);
      } else {
        // No row yet — create with defaults
        const { error: insertErr } = await supabase
          .from('notification_preferences')
          .insert({ user_id: userId });
        if (insertErr) {
          reportError(insertErr, { context: 'NotificationSettings.createDefaults', silent: true });
        }
        // All defaults are true
        const defaults: Prefs = {};
        getAllPrefKeys().forEach((key) => {
          defaults[key] = true;
        });
        setPrefs(defaults);
      }
      setLoading(false);
    })();
  }, [userId]);

  const togglePref = async (key: string, value: boolean) => {
    if (!userId || !prefs) return;

    // Optimistic update
    setPrefs((prev) => prev ? { ...prev, [key]: value } : prev);
    setSaving(key);

    if (!mode.isMock) {
      const { error } = await supabase
        .from('notification_preferences')
        .update({ [key]: value, updated_at: new Date().toISOString() })
        .eq('user_id', userId);

      if (error) {
        // Revert on failure
        setPrefs((prev) => prev ? { ...prev, [key]: !value } : prev);
        reportError(error, { context: `NotificationSettings.toggle.${key}` });
      }
    }
    setSaving(null);
  };

  // Build sections: delivery toggle first, then role-specific groups
  const roleSections = PREFS_BY_ROLE[userRole ?? 'startup'] ?? PREFS_BY_ROLE.startup;
  const sections: { title: string; items: PrefItem[] }[] = [
    { title: 'Delivery', items: DELIVERY_PREFS },
    ...roleSections,
  ];

  const content = loading ? (
    <View className="flex-1 items-center justify-center">
      <ActivityIndicator size="large" color={colors.primary[600]} />
    </View>
  ) : (
    <ScrollView
      className="flex-1"
      showsVerticalScrollIndicator={false}
      contentContainerStyle={{ paddingBottom: 40 }}
    >
      <View className="px-4 pt-4">
        <Text style={{ color: colors.text.secondary }} className="text-sm mb-4">
          Choose which notifications you want to receive. Changes take effect immediately.
        </Text>

        {sections.map((section, si) => (
          <Animated.View key={section.title} entering={FadeInDown.delay(si * 80).duration(400)} className="mb-6">
            <Text style={{ color: colors.text.tertiary }} className="text-xs font-semibold uppercase tracking-wide mb-2 ml-1">
              {section.title}
            </Text>
            <View className="rounded-2xl overflow-hidden" style={{ backgroundColor: colors.surface.primary }}>
              {section.items.map((item, i) => (
                <View
                  key={item.key}
                  className="flex-row items-center px-4 py-3.5"
                  style={i < section.items.length - 1 ? { borderBottomWidth: 1, borderBottomColor: colors.border.light } : undefined}
                >
                  <View className="flex-1 mr-3">
                    <Text style={{ color: colors.text.primary }} className="text-sm font-medium" numberOfLines={1}>
                      {item.label}
                    </Text>
                    <Text style={{ color: colors.text.tertiary }} className="text-xs mt-0.5" numberOfLines={2}>
                      {item.description}
                    </Text>
                  </View>
                  <Switch
                    value={prefs?.[item.key] !== false}
                    onValueChange={(val) => togglePref(item.key, val)}
                    trackColor={{ false: colors.border.medium, true: colors.primary[600] }}
                    thumbColor="#FFFFFF"
                    disabled={saving === item.key}
                  />
                </View>
              ))}
            </View>
          </Animated.View>
        ))}
      </View>
    </ScrollView>
  );

  if (isWeb) {
    const breadcrumbs = userRole === 'admin'
      ? [{ label: 'Profile', href: '/(admin)/profile' }, { label: 'Notification Settings' }]
      : userRole === 'startup'
      ? [{ label: 'Profile', href: '/(startup)/userprofile' }, { label: 'Notification Settings' }]
      : [{ label: 'Profile', href: '/(investor)/investorprofile' }, { label: 'Notification Settings' }];

    return (
      <AppLayout title="Notification Settings" breadcrumbs={breadcrumbs}>
        {content}
      </AppLayout>
    );
  }

  return (
    <>
      <Stack.Screen
        options={{
          title: 'Notification Settings',
          headerShown: true,
          headerStyle: { backgroundColor: colors.surface.primary },
          headerTintColor: colors.text.primary,
          headerLeft: () => (
            <AnimatedPressable onPress={() => router.back()} className="p-2 mr-2">
              <ChevronLeft size={24} color={colors.text.primary} />
            </AnimatedPressable>
          ),
        }}
      />
      <SafeAreaView className="flex-1" style={{ backgroundColor: colors.surface.secondary }} edges={['bottom']}>
        {content}
      </SafeAreaView>
    </>
  );
}
